import React, { useState, useEffect, lazy, Suspense } from "react";
import {
    Box,
    Divider,
    CircularProgress,
    Typography,
    Button,
} from "@mui/material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import useEditTimeTracker from "./useEditTimeTracker";
import useApi from "../useApi";

import "./DocumentProcessor.css";
import "./document_styles.css";
import PDFViewer from "./PDFViewer";

const MIN_WIDTH_PERCENT = 10;

// UNPROCESSABLE means the document generated but one or more pages were replaced
// with a "PAGE FAILED" placeholder. The JSON is still valid and editable, so the
// editor opens as normal — the placeholder pages flag themselves in place.
const isDocumentReady = (documentInfo) =>
    documentInfo?.llm_processing_status === "SUCCESS" ||
    documentInfo?.llm_processing_status === "UNPROCESSABLE";

// Dynamically import JsonEditor
const JsonEditor = lazy(() => import("./JsonEditor"));

const ProcessingStatus = ({ status }) => {
    const getStatusColor = () => {
        switch (status) {
            case "SUCCESS":
                return "green";
            case "FAILURE":
            case "UNPROCESSABLE":
                return "red";
            default:
                return "orange";
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
        >
            <CircularProgress color="primary" size={60} />
            <Typography
                variant="h6"
                style={{ marginTop: 16, color: getStatusColor() }}
            >
                {status === "SUCCESS"
                    ? "Processing Complete"
                    : `Processing Status: ${status}`}
            </Typography>
        </Box>
    );
};

const RenderedPdfPanel = ({
    renderedPdf,
    renderLoading,
    isStale,
    onRender,
    onDownload,
    canRender,
}) => (
    <>
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            gap={1}
        >
            <h2 className="json-preview-header" style={{ margin: 0 }}>
                Preview
                {isStale && renderedPdf && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: "warning.main" }}
                    >
                        (out of date)
                    </Typography>
                )}
            </h2>
            <Box display="flex" gap={1}>
                <Button
                    onClick={onDownload}
                    variant="outlined"
                    size="small"
                    disabled={!renderedPdf}
                >
                    Download PDF
                </Button>
                <Button
                    onClick={() => onRender()}
                    variant="contained"
                    size="small"
                    disabled={renderLoading || !canRender}
                >
                    {renderLoading ? "Rendering..." : "Re-render PDF"}
                </Button>
            </Box>
        </Box>
        <Box position="relative" height="100%">
            {renderedPdf ? (
                <PDFViewer file={renderedPdf} />
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    color="text.secondary"
                >
                    {renderLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Rendering preview...
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2">
                            Preview will appear here.
                        </Typography>
                    )}
                </Box>
            )}
            {renderLoading && renderedPdf && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        backgroundColor: "rgba(255,255,255,0.6)",
                        zIndex: 5,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={24} />
                        <Typography variant="body2">
                            Rendering preview...
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    </>
);

const DCADocumentProcessor = () => {
    const { documentId } = useParams();
    const [panelWidths, setPanelWidths] = useState({
        left: 30,
        middle: 40,
        right: 30,
    });
    const [documentInfo, setDocumentInfo] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [renderedPdf, setRenderedPdf] = useState(null);
    const [isStale, setIsStale] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [autoRenderDone, setAutoRenderDone] = useState(false);
    const [{ loading: infoLoading }, callInfoApi] = useApi();
    const [{ loading: fileLoading }, callFileApi] = useApi();
    const [{ loading: saveLoading }, callSaveApi] = useApi();
    const [{ loading: renderLoading }, callRenderApi] = useApi();
    const [copyButtonText, setCopyButtonText] = useState("Copy JSON");
    const { startEditing, recordActivity, getAndResetTimeSpent } =
        useEditTimeTracker();

    useEffect(() => {
        if (documentId) {
            fetchDocumentInfo();
            fetchPdfFile();
        }
    }, [documentId]);

    useEffect(() => {
        // Set up polling to check document status
        const pollInterval = setInterval(() => {
            if (
                documentInfo &&
                !["SUCCESS", "FAILURE", "UNPROCESSABLE"].includes(
                    documentInfo.llm_processing_status,
                )
            ) {
                fetchDocumentInfo();
            }
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(pollInterval);
    }, [documentInfo]);

    // Auto-render the PDF preview once the document is ready.
    useEffect(() => {
        if (
            !autoRenderDone &&
            isDocumentReady(documentInfo) &&
            documentInfo?.edited_json
        ) {
            setAutoRenderDone(true);
            if (documentInfo.llm_processing_status === "UNPROCESSABLE") {
                toast.warning(
                    'Some pages could not be generated and are marked "PAGE FAILED" in the document. Please review them.',
                );
            }
            handleRender(documentInfo.edited_json);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentInfo?.llm_processing_status, documentInfo?.edited_json]);

    // Warn before closing the tab with unsaved changes.
    useEffect(() => {
        if (!isDirty) return undefined;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const reorderKeys = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(reorderKeys);
        } else if (typeof obj === "object" && obj !== null) {
            const reordered = {};
            const keys = Object.keys(obj);
            // Move 'children' to the end
            keys.filter((key) => key !== "children").forEach((key) => {
                reordered[key] = reorderKeys(obj[key]);
            });
            if (keys.includes("children")) {
                reordered["children"] = reorderKeys(obj["children"]);
            }
            return reordered;
        }
        return obj;
    };

    const fetchDocumentInfo = async () => {
        const result = await callInfoApi({
            url: `/api/dca/documents/${documentId}`,
        });
        if (result.error) {
            toast.error(`Failed to fetch document info: ${result.error}`);
        } else {
            result.edited_json = reorderKeys(result.edited_json);
            setDocumentInfo(result);
            startEditing(); // Start tracking edit time when document is loaded
        }
    };

    const fetchPdfFile = async () => {
        const result = await callFileApi({
            url: `/api/dca/documents/${documentId}/file`,
            responseType: "blob",
        });
        if (result.error) {
            toast.error(`Failed to fetch PDF file: ${result.error}`);
        } else {
            setPdfFile(result);
        }
    };

    const handleDrag = (event, direction) => {
        event.preventDefault();
        const clientX = event.clientX;
        const windowWidth = window.innerWidth;

        setPanelWidths((prevWidths) => {
            let newWidths = { ...prevWidths };
            if (direction === "left") {
                newWidths.left = Math.max(
                    MIN_WIDTH_PERCENT,
                    Math.min(
                        100 - MIN_WIDTH_PERCENT - prevWidths.middle,
                        (clientX / windowWidth) * 100,
                    ),
                );
                newWidths.middle = 100 - newWidths.left - prevWidths.right;
            } else if (direction === "right") {
                newWidths.right = Math.max(
                    MIN_WIDTH_PERCENT,
                    Math.min(
                        100 - MIN_WIDTH_PERCENT - prevWidths.left,
                        ((windowWidth - clientX) / windowWidth) * 100,
                    ),
                );
                newWidths.middle = 100 - prevWidths.left - newWidths.right;
            }
            return newWidths;
        });
    };

    const handleJsonUpdate = (newData) => {
        try {
            const parsedData = JSON.parse(newData);
            setDocumentInfo((prevInfo) => ({
                ...prevInfo,
                edited_json: parsedData,
            }));
            setIsStale(true);
            setIsDirty(true);
            recordActivity(); // Record editing activity
        } catch (error) {
            console.error("Failed to parse JSON:", error);
        }
    };

    const handleRender = async (jsonOverride) => {
        const json = jsonOverride ?? documentInfo?.edited_json;
        if (!json) {
            toast.error("No JSON to render.");
            return;
        }
        try {
            const blob = await callRenderApi({
                url: `/api/dca/documents/${documentId}/render`,
                method: "POST",
                body: { edited_json: json },
                responseType: "blob",
            });
            if (blob) {
                setRenderedPdf(blob);
                setIsStale(false);
            }
        } catch (error) {
            // useApi already shows a toast on failure; just log here.
            console.error("Error rendering PDF:", error);
        }
    };

    const handleDownloadPdf = () => {
        if (!renderedPdf) return;
        const url = URL.createObjectURL(renderedPdf);
        const a = document.createElement("a");
        a.href = url;
        const baseName = (documentInfo?.name || "document").replace(
            /\.pdf$/i,
            "",
        );
        a.download = `${baseName}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadJson = () => {
        if (!documentInfo?.edited_json) return;
        const blob = new Blob(
            [JSON.stringify(documentInfo.edited_json, null, 2)],
            { type: "application/json" },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const baseName = (documentInfo?.name || "document").replace(
            /\.pdf$/i,
            "",
        );
        a.download = `${baseName}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleCopy = () => {
        const textToCopy = JSON.stringify(documentInfo.edited_json, null, 2);
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopyButtonText("Copied!");
                setTimeout(() => setCopyButtonText("Copy JSON"), 2000);
            })
            .catch((error) => {
                console.error("Failed to copy:", error);
                toast.error("Failed to copy JSON.");
            });
    };

    const handleSave = async () => {
        if (!documentInfo || !documentInfo.edited_json) {
            toast.error("No data to save.");
            return;
        }

        const timeSpentSinceLastSave = getAndResetTimeSpent();

        try {
            const result = await callSaveApi({
                url: `/api/dca/documents/${documentId}`,
                method: "PUT",
                body: {
                    edited_json: documentInfo.edited_json,
                    extra_time_spent: timeSpentSinceLastSave,
                },
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (result) {
                toast.success("Saved — your work will survive a reload.");
                setIsDirty(false);
                handleRender(documentInfo.edited_json);
            }
        } catch (error) {
            console.error("Error saving document:", error);
        }
    };

    const handleMouseDown = (event, direction) => {
        window.onmousemove = (e) => handleDrag(e, direction);
        window.onmouseup = () => {
            window.onmousemove = window.onmouseup = null;
        };
    };

    const renderContent = () => {
        if (!documentInfo || !isDocumentReady(documentInfo)) {
            return (
                <ProcessingStatus
                    status={
                        documentInfo
                            ? documentInfo.llm_processing_status
                            : "PENDING"
                    }
                />
            );
        }

        return (
            <Suspense fallback={<ProcessingStatus status="LOADING" />}>
                <Box
                    className="json-editor-panel"
                    style={{ width: `${panelWidths.middle}%` }}
                >
                    <JsonEditor
                        documentInfo={documentInfo}
                        onUpdate={handleJsonUpdate}
                        onCopy={handleCopy}
                        onDownload={handleDownloadJson}
                        onSave={handleSave}
                        copyButtonText={copyButtonText}
                        saveLoading={saveLoading}
                        loading={infoLoading || fileLoading || saveLoading}
                        isDirty={isDirty}
                    />
                </Box>
                <Divider
                    orientation="vertical"
                    className="document-processor-divider"
                    onMouseDown={(e) => handleMouseDown(e, "right")}
                />
                <Box
                    className="json-preview"
                    style={{ width: `${panelWidths.right}%` }}
                >
                    <RenderedPdfPanel
                        renderedPdf={renderedPdf}
                        renderLoading={renderLoading}
                        isStale={isStale}
                        onRender={handleRender}
                        onDownload={handleDownloadPdf}
                        canRender={!!documentInfo?.edited_json}
                    />
                </Box>
            </Suspense>
        );
    };

    return (
        <Box className="document-processor-container">
            <Box className="document-processor-content">
                <Box
                    className="document-preview-left-panel"
                    style={{ width: `${panelWidths.left}%` }}
                >
                    {pdfFile && <PDFViewer file={pdfFile} />}
                </Box>
                <Divider
                    orientation="vertical"
                    className="document-processor-divider"
                    onMouseDown={(e) => handleMouseDown(e, "left")}
                />
                {renderContent()}
            </Box>
        </Box>
    );
};

export default DCADocumentProcessor;
export { JsonEditor };
